import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, PanelGroup } from 'rsuite';
import { AxiosError } from 'axios';
import ActionPanel from '@/components/ActionPanel';
import PageContent from '@/components/PageContent';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { ClubEventStatusEnum, RunningClubEvent, UpsertRunningClubEvent } from '@/models/running-club-event.model';
import { ClubEventActivitySyncResult, deleteRunningClubEvent, setRunningClubEventConfirmed, syncRunningClubEventActivities, upsertRunningClubEvent } from '@/services/running-club-event.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';

interface RunningClubEventActionsProps {
  formObject?: RunningClubEvent;
  onChanged?: () => void;
};

const RunningClubEventActions: React.FC<RunningClubEventActionsProps> = ({ formObject, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const nav = useNavigate();

  const handleDuplicate = async () => {
    if (!formObject) return;
    setLoading(true);
    try {
      const duplicate: UpsertRunningClubEvent = {
        id: undefined,
        name: `${formObject.name} (Copy)`,
        description: formObject.description,
        coverImage: formObject.coverImage,
        startTime: formObject.startTime,
        startAddress: formObject.startAddress,
        endTime: formObject.endTime,
        startLat: formObject.startLat,
        startLng: formObject.startLng,
        endLat: formObject.endLat,
        endLng: formObject.endLng,
        jsonPath: formObject.jsonPath,
        visibility: formObject.visibility,
        status: ClubEventStatusEnum.Draft,
        customRoleIds: formObject.customRoleIds ?? [],
        runningClubId: formObject.runningClubId,
      };
      const result: BaseResponseModel<RunningClubEvent> = await upsertRunningClubEvent(duplicate);
      if (result.success) {
        toastSuccessMessage('Event duplicated as a draft.');
        nav(`/running-clubs/events/${result.result.id}`, {
          state: {
            tab: 'running-club-event-form',
            initValue: { runningClubId: formObject.runningClubId },
          },
        });
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(errorModel ?? 'Error in duplicating the event.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!formObject?.id) return;
    setConfirming(true);
    try {
      const isConfirmed = !formObject.isConfirmedByClub;
      const result: BaseResponseModel<null> = await setRunningClubEventConfirmed(formObject.id, isConfirmed);
      if (result.success) {
        toastSuccessMessage(isConfirmed ? 'Event confirmed for the club.' : 'Event marked as not confirmed.');
        onChanged?.();
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(errorModel ?? 'Error in updating the event confirmation.');
    } finally {
      setConfirming(false);
    }
  };

  const handleSyncActivities = async () => {
    if (!formObject?.id) return;
    setSyncing(true);
    try {
      const result: BaseResponseModel<ClubEventActivitySyncResult> = await syncRunningClubEventActivities(formObject.id);
      if (result.success) {
        const { matchedCount, linkedCount, skippedAlreadyLinkedCount, checkedInCount, heatCellCount } = result.result;
        const summary = `${matchedCount} matching activities, ${linkedCount} newly linked, ${skippedAlreadyLinkedCount} already linked elsewhere. ${checkedInCount} checked-in runners, ${heatCellCount} heatmap cells.`;
        if (heatCellCount > 0) toastSuccessMessage(summary);
        else toastErrorMessage(`${summary} No heatmap: the event needs at least one checked-in runner.`);
        onChanged?.();
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(errorModel ?? 'Error in syncing activities.');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!formObject?.id) return;
    setDeleting(true);
    try {
      const result: BaseResponseModel<null> = await deleteRunningClubEvent(formObject.id);
      if (result.success) {
        toastSuccessMessage('Event deleted.');
        setConfirmingDelete(false);
        nav('/running-clubs/events');
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(errorModel ?? 'Error in deleting the event.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PageContent>
      <PanelGroup>
        {formObject && (formObject.isConfirmedByClub ? (
          <ActionPanel
            title={'Unconfirm'}
            description="This event is confirmed by the club. Unconfirming marks it as not the club's own event in the app, the same as an automatically published event the club hasn't confirmed yet."
            onClick={handleConfirm}
            loading={confirming}
            color="orange"
          />
        ) : (
          <ActionPanel
            title={'Confirm'}
            description={`${formObject.externalSourceUrl ? `Published automatically from ${formObject.externalSourceUrl} and n` : 'N'}ot yet confirmed by the club. Confirming marks it as the club's own event in the app.`}
            onClick={handleConfirm}
            loading={confirming}
            color="green"
          />
        ))}
        <ActionPanel
          title={'Duplicate'}
          description='Creates a copy of this event with the same details, in Draft status, and opens it for editing. Participants, activities and check-ins are not copied.'
          onClick={handleDuplicate}
          loading={loading}
          disabled={!formObject}
        />
        <ActionPanel
          title={'Sync Possible Activities'}
          description="Checks this event's route and time window against users' synced activities, links every match that isn't already linked to an event, marks those users as checked in, and regenerates the event's heatmap. Only works for Active or Finished events with a route."
          onClick={handleSyncActivities}
          loading={syncing}
          disabled={!formObject?.id}
        />
        <ActionPanel
          title={'Delete'}
          description='Soft deletes this event. It is hidden from the app and admin lists, its channel is removed, and check-in activity records it generated are voided.'
          onClick={() => setConfirmingDelete(true)}
          loading={deleting}
          disabled={!formObject?.id}
        />
      </PanelGroup>

      <Modal open={confirmingDelete} onClose={() => setConfirmingDelete(false)} size="xs">
        <Modal.Header><Modal.Title>Delete this event?</Modal.Title></Modal.Header>
        <Modal.Body>
          {formObject?.name} will be soft deleted, its channel removed, and every check-in activity
          it generated voided.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setConfirmingDelete(false)} appearance="subtle">Back</Button>
          <Button onClick={handleDelete} appearance="primary" color="red" loading={deleting}>
            Delete event
          </Button>
        </Modal.Footer>
      </Modal>
    </PageContent>
  );
};

export default RunningClubEventActions;
